import { getDocumentIdToQueueItemMap } from '@/helpers/queues';
import { Badge, Box, Button, toast, useOpenClose } from '@palmetto/palmetto-components';
import { useMemo } from 'react';

import {
  DocumentFile,
  DocumentDocument,
  DocumentStatus,
  DocumentTypes,
  documentTitles,
  UserPermissions,
} from 'types';
import { QueueDocument, QueueStatus, QueueRejectionReasons, QueueRejectionReason } from 'types';

import { useQueueClaimConflict } from '../../hooks/useQueueClaimConflict';
import { useArchiveDocumentMutation, useUpdateDocumentStatusMutation } from '../../services/documents';
import { isErrorWithData } from '../../services/helpers';
import { useLazyGetQueuesByAccountIdQuery, useUpdateQueueMutation } from '../../services/queues';
import { documentStatus } from '../../types/Documents';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import RequirePalmettoFinanceUser from '../auth/RequirePalmettoFinanceUser';
import RequirePermissions from '../auth/requirePermissions';
import { DocumentRejectionModal } from '../DocumentRejectionModal/DocumentRejectionModal';
import { FileItem } from '../documents/FileItem';

export function DocumentItem({
  item,
  showDocumentActions = true,
  allowArchive = true,
  queue,
  rejections = [],
}: {
  item: DocumentDocument;
  showDocumentActions?: boolean;
  allowArchive?: boolean;
  queue?: QueueDocument;
  rejections?: QueueRejectionReasons[];
}) {
  const { type, status, externalReference, files } = item;
  const { isOpen: isModalOpen, handleOpen: openModal, handleClose: closeModal } = useOpenClose();
  const [updateDocumentStatus, { isLoading: isUpdatingStatus }] = useUpdateDocumentStatusMutation();
  const [updateQueue, { isLoading: isUpdatingQueue }] = useUpdateQueueMutation();
  const [archiveDocument, { isLoading: isArchiving }] = useArchiveDocumentMutation();
  const [fetchQueuesByAccountId, { isFetching: isFetchingQueue }] = useLazyGetQueuesByAccountIdQuery();
  const { user } = useAuth();
  const documentTitle = documentTitles[type] || (type as string);
  const allowsAction = useIsOrgReadOnly();
  const canArchive =
    allowArchive && status !== DocumentStatus.approved && status !== DocumentStatus.rejected && allowsAction;

  const { checkClaimAndProceed, claimConflictModal } = useQueueClaimConflict(queue);

  // Re-fetch for a fresh assignee before the conflict check. A second pending document of the same
  // type never gets added to the queue's referenceIds until the first is reviewed, so byDocumentId
  // can miss it — fall back to matching by type. The type match can pick the wrong queue when an
  // account has more than one of this type, but handleQueueUpdate always mutates `queue.id` (the
  // original prop), so a mismatch here only affects the conflict check, never which queue is updated.
  const resolveLatestQueue = async (): Promise<QueueDocument | undefined> => {
    if (!queue) return queue;

    const accountId = item.accountId?.toString();
    if (!accountId) return queue;

    try {
      const freshQueues = await fetchQueuesByAccountId({ accountId });
      const byDocumentId = getDocumentIdToQueueItemMap(freshQueues)[item.id?.toString() ?? ''];
      return byDocumentId ?? freshQueues?.find((q: any) => q.type === queue.type) ?? queue;
    } catch {
      return queue;
    }
  };

  const getReasonLabel = (reason: string): string => {
    return (QueueRejectionReason as Record<string, string>)[reason];
  };

  const labelsPerRejection = useMemo(
    () => rejections.map((rej) => (rej.reasons ?? []).map(getReasonLabel).join(', ')),
    [rejections],
  );

  const handleDocumentStatusUpdate = async (
    status: DocumentStatus,
    rejectionNote?: string,
    rejectionReasons?: string[],
  ) => {
    try {
      await updateDocumentStatus({
        id: item?.id?.toString() || '',
        accountId: item?.accountId?.toString() || '',
        status,
        rejectionNote,
        rejectionReasons,
      }).unwrap();
      toast.success('Document updated successfully');
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error updating document: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error updating document');
      }
    }
  };

  const handleQueueUpdate = async (status: QueueStatus, note?: string, reasons?: QueueRejectionReason[]) => {
    try {
      if (queue?.id) {
        await updateQueue({
          id: queue.id,
          data: {
            assigneeId: user?.sub,
            status,
            ...(reasons && {
              rejectionReasons: [
                {
                  reasons,
                  rejectedDocumentId: item?.id,
                  ...(note && { note }),
                },
              ],
            }),
          },
        }).unwrap();
        toast.success('Queue updated successfully');
      }
    } catch (e) {
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        toast.error(`Error updating queue: ${errorMessage}`);
      } else {
        console.error(e);
        toast.error('Error updating queue');
      }
    }
  };

  const approve = async () => {
    const confirm = window.confirm('Are you sure you want to approve this document? This action cannot be undone.');
    if (!confirm) {
      return;
    }
    if (queue) {
      const resolved = await resolveLatestQueue();
      return checkClaimAndProceed(() => handleQueueUpdate(QueueStatus.approved), resolved);
    }
    // older accounts will not have a queue document
    return handleDocumentStatusUpdate(DocumentStatus.approved);
  };

  // handles older accounts without a queue document, newer accounts will use a rejection modal that calls handleRejectModal
  const reject = async () => {
    const confirm = window.confirm('Are you sure you want to reject this document? This action cannot be undone.');
    if (!confirm) {
      return;
    }
    await handleDocumentStatusUpdate(DocumentStatus.rejected);
  };

  const handleRejectModal = async (values: { rejectionReason: string; notes: string | undefined }) => {
    const { rejectionReason, notes } = values;
    await handleQueueUpdate(QueueStatus.rejected, notes, [rejectionReason as QueueRejectionReason]);
    closeModal();
  };

  const handleRejectButtonClick = async () => {
    const resolved = await resolveLatestQueue();
    checkClaimAndProceed(openModal, resolved);
  };

  const archive = async () => {
    const confirmed = window.confirm(
      'Are you sure you want to Archive this Document? This action cannot be undone.',
    );

    if (!confirmed) return;

    await archiveDocument({
      id: item?.id?.toString() || '',
      accountId: item?.accountId?.toString() || '',
      invalidateAllTags: false,
    });

    toast.success('Document archived successfully');
  };

  return (
    <>
      {claimConflictModal}
      <DocumentRejectionModal
        isModalOpen={isModalOpen}
        title={documentTitle}
        handleClose={closeModal}
        handleSubmit={handleRejectModal}
      />
      <Box
        padding="sm 0 sm lg"
        width="100"
        childGap="lg"
        direction="row"
        alignItems="center"
        borderWidth="xs 0 0 0"
        borderColor="separator"
      >
        <Box
          width="100"
          direction={{ base: 'column', tablet: 'row' }}
          alignItems={{ base: 'flex-start', tablet: 'center' }}
          childGap={{ base: 'xs', tablet: 'lg' }}
        >
          <Box alignItems="flex-start" width="xl">
            <Badge message={documentStatus[status]?.label} variant={documentStatus[status]?.variant} />
          </Box>
          <Box flex="auto">
            <Box display="block" childGap="2xs">
              {files.map((f: DocumentFile) => (
                <FileItem file={f} key={f?.md5Hash} />
              ))}
            </Box>

            {externalReference && (
              <Box fontSize="xs" margin="xs 0 0 0">{`Ext. Reference: ${externalReference}`}</Box>
            )}
            {status === DocumentStatus.rejected && rejections.length > 0 && (
              <Box color="danger" margin="xs 0 0 0">
                {rejections.map((rej, idx) => (
                  <Box key={idx}>
                    {rej.note ? `${labelsPerRejection[idx]}: ${rej.note}` : labelsPerRejection[idx]}
                  </Box>
                ))}
              </Box>
            )}
          </Box>
        </Box>

        {showDocumentActions && (
          <Box
            width="40"
            direction={{ base: 'column', tablet: 'row' }}
            childGap="xs"
            justifyContent="flex-end"
            alignItems="flex-end"
          >
            {status === DocumentStatus.pending && (
              <div>
                <RequirePalmettoFinanceUser>
                  <RequirePermissions permissions={[UserPermissions.lightReachSupport]} checkAllPermissions={false}>
                    <Button
                      size="sm"
                      variant="primary"
                      isLoading={isUpdatingStatus || isUpdatingQueue || isArchiving}
                      onClick={approve}
                      disabled={!allowsAction}
                    >
                      Approve
                    </Button>
                  </RequirePermissions>
                </RequirePalmettoFinanceUser>
              </div>
            )}
            {type !== DocumentTypes.contract && status === DocumentStatus.pending && (
              <div>
                <RequirePalmettoFinanceUser>
                  <RequirePermissions permissions={[UserPermissions.lightReachSupport]} checkAllPermissions={false}>
                    <Button
                      size="sm"
                      variant="primary"
                      tone="danger"
                      isLoading={isUpdatingStatus || isUpdatingQueue || isArchiving || isFetchingQueue}
                      onClick={queue ? handleRejectButtonClick : reject}
                      disabled={!allowsAction}
                    >
                      Reject
                    </Button>
                  </RequirePermissions>
                </RequirePalmettoFinanceUser>
              </div>
            )}
            {type === DocumentTypes.contract && status === DocumentStatus.pending && (
              <div>
                <RequirePermissions
                  permissions={[UserPermissions.admin, UserPermissions.editor]}
                  checkAllPermissions={false}
                >
                  <Button
                    size="sm"
                    variant="primary"
                    tone="danger"
                    isLoading={isUpdatingStatus || isUpdatingQueue || isArchiving}
                    onClick={reject}
                  >
                    Void
                  </Button>
                </RequirePermissions>
              </div>
            )}
            {canArchive && (
              <RequirePermissions
                permissions={[UserPermissions.admin, UserPermissions.editor]}
                checkAllPermissions={false}
              >
                <Button
                  size="sm"
                  variant="secondary"
                  tone="neutral"
                  isLoading={isUpdatingStatus || isUpdatingQueue || isArchiving}
                  onClick={archive}
                >
                  Archive
                </Button>
              </RequirePermissions>
            )}
          </Box>
        )}
      </Box>
    </>
  );
}
